// Tremor LineChart [v1.0.0]
/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import React, { useCallback } from "react";
import {
  CartesianGrid,
  Dot,
  Label,
  Line,
  Legend as RechartsLegend,
  LineChart as RechartsLineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import {
  AvailableChartColors,
  constructCategoryColors,
  getColorClassName,
  hasOnlyOneValueForKey,
  type AvailableChartColorsKeys,
  calculateDynamicMargins,
  measureTextWidth,
} from "@/lib/chartUtils";
import { calculateNiceYAxisDomain, generateCustomTicks } from "@/lib/chartUtils";
import { cn } from "@/lib/utils";
import { ChartLegend } from "./components/ChartLegend";
import { ChartTooltip, type TooltipProps } from "./components/ChartTooltip";
import { createCustomTick } from "./components/CustomTick";
//#region Legend - Using shared component

//#region Tooltip - Using shared component

//#region LineChart

interface ActiveDot {
  index?: number;
  dataKey?: string;
}

type BaseEventProps = {
  eventType: "dot" | "category";
  categoryClicked: string;
  [key: string]: number | string;
};

type LineChartEventProps = BaseEventProps | null | undefined;

interface DotClickItemData {
  index?: number;
  dataKey?: string;
  payload?: Record<string, unknown>;
}

interface RechartsTooltipPayloadItem {
  dataKey?: string | number;
  value?: number;
  payload?: Record<string, unknown>;
  type?: string;
}

interface RechartsLegendPayloadItem {
  value?: string;
  color?: string;
}

interface ActiveDotProps {
  cx?: number;
  cy?: number;
  strokeLinecap?: string;
  strokeLinejoin?: string;
  strokeWidth?: number;
  dataKey?: string;
  index?: number;
  payload?: Record<string, unknown>;
}

interface LineChartProps extends React.HTMLAttributes<HTMLDivElement> {
  data: Record<string, any>[];
  index: string;
  categories: string[];
  colors?: AvailableChartColorsKeys[];
  valueFormatter?: (value: number) => string;
  startEndOnly?: boolean;
  showXAxis?: boolean;
  showYAxis?: boolean;
  showGridLines?: boolean;
  yAxisWidth?: number;
  intervalType?: "preserveStartEnd" | "equidistantPreserveStart" | 0;
  tickCount?: number;
  showTooltip?: boolean;
  showLegend?: boolean;
  autoMinValue?: boolean;
  minValue?: number;
  maxValue?: number;
  allowDecimals?: boolean;
  onValueChange?: (value: LineChartEventProps) => void;
  enableLegendSlider?: boolean;
  tickGap?: number;
  connectNulls?: boolean;
  xAxisLabel?: string;
  yAxisLabel?: string;
  legendPosition?: "left" | "center" | "right";
  tooltipCallback?: (tooltipCallbackContent: TooltipProps) => void;
  customTooltip?: React.ComponentType<TooltipProps>;
  strokeColors?: string[];
  chartMargin?: {
    top?: number;
    right?: number;
    bottom?: number;
    left?: number;
  };
  customLegend?: React.ComponentType<{
    payload?: Array<{
      value: string;
      color: string;
    }>;
    onClick?: (name: string) => void;
    activeLegend?: string;
  }>;
}

const LineChart = React.forwardRef<HTMLDivElement, LineChartProps>((props, ref) => {
  const {
    data = [],
    categories = [],
    index,
    colors = AvailableChartColors,
    valueFormatter = (value: number) => {
      const formatter = new Intl.NumberFormat("en-US", {
        notation: "compact",
        compactDisplay: "short",
      });
      return formatter.format(value);
    },
    startEndOnly = false,
    showXAxis = true,
    showYAxis = true,
    showGridLines = true,
    yAxisWidth = 20,
    intervalType = "equidistantPreserveStart",
    showTooltip = true,
    showLegend = true,
    autoMinValue = false,
    minValue,
    maxValue,
    allowDecimals = true,
    connectNulls = false,
    className,
    onValueChange,
    enableLegendSlider = false,
    tickGap = 2,
    tickCount,
    xAxisLabel,
    yAxisLabel,
    legendPosition = "right",
    tooltipCallback,
    customTooltip,
    strokeColors,
    chartMargin,
    customLegend,
    ...other
  } = props;
  const CustomTooltip = customTooltip;
  const paddingValue =
    (!showXAxis && !showYAxis) || (startEndOnly && !showYAxis) ? 0 : 20;

  // When there are very few data points (≤2), use "preserveStartEnd" to ensure
  // both first and last ticks are visible on the x-axis
  const effectiveIntervalType = data.length <= 30 ? 0 : intervalType;
  const [legendHeight, setLegendHeight] = React.useState(0);
  const [activeDot, setActiveDot] = React.useState<ActiveDot | undefined>(undefined);
  const [activeLegend, setActiveLegend] = React.useState<string | undefined>(undefined);
  const [shouldRotateTicks, setShouldRotateTicks] = React.useState(true);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const categoryColors = constructCategoryColors(categories, colors);

  // Calculate if labels fit horizontally based on ACTUAL rendered ticks from Recharts
  React.useEffect(() => {
    const calculateRotation = () => {
      if (!containerRef.current || !data?.length) {
        setShouldRotateTicks(false);
        return;
      }

      // Wait for DOM to be fully rendered
      // Use double RAF to ensure Recharts has finished rendering
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const svg = containerRef.current?.querySelector("svg");
          if (!svg) {
            setShouldRotateTicks(true);
            return;
          }

          // Find all x-axis tick text elements that Recharts actually rendered
          const xAxisGroup = svg.querySelector(".recharts-xAxis");
          if (!xAxisGroup) {
            setShouldRotateTicks(true);
            return;
          }

          const tickTexts = Array.from(xAxisGroup.querySelectorAll("text"));

          if (tickTexts.length === 0) {
            setShouldRotateTicks(true);
            return;
          }

          // Get the actual positions and widths of visible ticks
          const tickInfo = tickTexts
            .map((textEl) => {
              const transform = textEl.parentElement?.getAttribute("transform");
              if (!transform) return null;

              // Extract x coordinate from transform="translate(x, y)"
              const match = transform.match(/translate\(([^,]+),/);
              const x = match ? parseFloat(match[1]) : null;
              const text = textEl.textContent || "";

              if (x === null || text === "") return null;

              // Measure the actual text width
              const textWidth = measureTextWidth(text, 12);

              return { x, text, textWidth };
            })
            .filter(
              (info): info is { x: number; text: string; textWidth: number } =>
                info !== null,
            )
            .sort((a, b) => a.x - b.x); // Sort by x position

          if (tickInfo.length < 2) {
            setShouldRotateTicks(true);
            return;
          }

          // Calculate actual spacing between adjacent visible ticks
          const spacings: number[] = [];
          for (let i = 0; i < tickInfo.length - 1; i++) {
            spacings.push(tickInfo[i + 1].x - tickInfo[i].x);
          }

          const minSpacing = Math.min(...spacings);
          const maxLabelWidth = Math.max(...tickInfo.map((t) => t.textWidth));

          // Labels can fit horizontally if minimum spacing is enough for the widest label
          // Add 5px buffer for visual clarity
          const canFit = minSpacing >= maxLabelWidth + 5;

          setShouldRotateTicks(!canFit);
        });
      });
    };

    calculateRotation();

    // Recalculate on window resize
    const handleResize = () => calculateRotation();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [data, index, chartMargin, xAxisLabel, yAxisLabel, tickGap]);

  // Handle ref forwarding - use containerRef for measurements
  React.useEffect(() => {
    if (containerRef.current && ref) {
      if (typeof ref === "function") {
        ref(containerRef.current);
      } else {
        ref.current = containerRef.current;
      }
    }
  }, [ref]);

  // Unified color system: use strokeColors if provided, otherwise use categoryColors
  const getColorForCategory = useCallback(
    (category: string, index: number) => {
      return strokeColors?.[index] || (categoryColors.get(category) as string);
    },
    [strokeColors, categoryColors],
  );

  const niceYAxisDomain = calculateNiceYAxisDomain(
    data,
    categories,
    autoMinValue,
    minValue,
    maxValue,
    5,
  );
  const customTicks = generateCustomTicks(niceYAxisDomain, 5, allowDecimals);
  const hasOnValueChange = !!onValueChange;
  const prevActiveRef = React.useRef<boolean | undefined>(undefined);
  const prevLabelRef = React.useRef<string | undefined>(undefined);

  // Create tick component with rotation state
  const CustomTickComponent = React.useMemo(
    () => createCustomTick(shouldRotateTicks),
    [shouldRotateTicks],
  );

  function onDotClick(itemData: DotClickItemData, event: React.MouseEvent) {
    event.stopPropagation();

    if (!hasOnValueChange || !itemData.dataKey) return;
    if (
      (itemData.index === activeDot?.index && itemData.dataKey === activeDot?.dataKey) ||
      (hasOnlyOneValueForKey(data, itemData.dataKey) &&
        activeLegend &&
        activeLegend === itemData.dataKey)
    ) {
      setActiveLegend(undefined);
      setActiveDot(undefined);
      onValueChange?.(null);
    } else {
      setActiveLegend(itemData.dataKey);
      setActiveDot({
        index: itemData.index,
        dataKey: itemData.dataKey,
      });
      const eventPayload: BaseEventProps = {
        eventType: "dot",
        categoryClicked: itemData.dataKey,
        ...(itemData.payload as Record<string, number | string>),
      };
      onValueChange?.(eventPayload);
    }
  }

  function onCategoryClick(dataKey: string) {
    if (!hasOnValueChange) return;
    if (
      (dataKey === activeLegend && !activeDot) ||
      (hasOnlyOneValueForKey(data, dataKey) && activeDot && activeDot.dataKey === dataKey)
    ) {
      setActiveLegend(undefined);
      onValueChange?.(null);
    } else {
      setActiveLegend(dataKey);
      onValueChange?.({
        eventType: "category",
        categoryClicked: dataKey,
      });
    }
    setActiveDot(undefined);
  }

  return (
    <div
      ref={containerRef}
      className={cn("h-80 w-full", className)}
      tremor-id="tremor-raw"
      {...other}
    >
      <ResponsiveContainer className="overflow-visible">
        <RechartsLineChart
          data={data}
          onClick={
            hasOnValueChange && (activeLegend || activeDot)
              ? () => {
                  setActiveDot(undefined);
                  setActiveLegend(undefined);
                  onValueChange?.(null);
                }
              : undefined
          }
          margin={calculateDynamicMargins(
            data,
            index,
            chartMargin,
            xAxisLabel,
            yAxisLabel,
          )}
          className="overflow-visible [&>svg]:overflow-visible"
        >
          {showGridLines ? (
            <CartesianGrid
              className={cn("stroke-gray-200 stroke-1 dark:stroke-gray-800")}
              horizontal={true}
              vertical={false}
            />
          ) : null}
          <XAxis
            padding={{ left: paddingValue, right: paddingValue }}
            hide={!showXAxis}
            dataKey={index}
            interval={effectiveIntervalType}
            tickCount={tickCount}
            tick={CustomTickComponent}
            fill=""
            stroke=""
            className={cn(
              // base
              "text-xs",
              // text fill
              "fill-gray-500 dark:fill-gray-500",
            )}
            tickLine={true}
            axisLine={false}
            minTickGap={tickGap}
            tickMargin={20}
          >
            {xAxisLabel && (
              <Label
                position="insideBottom"
                offset={-20}
                className="fill-gray-800 text-sm font-medium dark:fill-gray-200"
              >
                {xAxisLabel}
              </Label>
            )}
          </XAxis>
          <YAxis
            width={yAxisWidth}
            hide={!showYAxis}
            axisLine={false}
            tickLine={false}
            type="number"
            domain={niceYAxisDomain}
            ticks={customTicks}
            tick={{ transform: "translate(-3, 0)", color: "#6B7280" }}
            fill=""
            stroke=""
            className={cn(
              // base
              "text-xs",
              // text fill
              "fill-gray-500 dark:fill-gray-500",
            )}
            tickFormatter={valueFormatter}
            allowDecimals={allowDecimals}
          >
            {yAxisLabel && (
              <Label
                position="insideLeft"
                style={{ textAnchor: "middle" }}
                angle={-90}
                offset={-15}
                className="fill-gray-800 text-sm font-medium dark:fill-gray-200"
              >
                {yAxisLabel}
              </Label>
            )}
          </YAxis>
          <Tooltip
            wrapperStyle={{ outline: "none", zIndex: 50, pointerEvents: "none" }}
            isAnimationActive={true}
            animationDuration={100}
            cursor={{ stroke: "#d1d5db", strokeWidth: 1 }}
            offset={20}
            position={{ y: 0 }}
            content={({ active, payload, label }) => {
              const cleanPayload: TooltipProps["payload"] = payload
                ? payload
                    .map((item: unknown, index: number) => {
                      const payloadItem = item as RechartsTooltipPayloadItem;
                      // Find the category index to get the correct stroke color
                      const dataKey = String(payloadItem.dataKey ?? "");
                      const categoryIndex = categories.indexOf(dataKey);
                      return {
                        category: dataKey,
                        value: payloadItem.value ?? 0,
                        index: String(
                          (payloadItem.payload?.[index] as number | string) ?? "",
                        ),
                        color: getColorForCategory(dataKey, categoryIndex),
                        type: payloadItem.type,
                        payload: payloadItem.payload ?? {},
                      };
                    })
                    .filter((item) => item.category !== "")
                : [];

              const labelString = String(label ?? "");

              if (
                tooltipCallback &&
                (active !== prevActiveRef.current || labelString !== prevLabelRef.current)
              ) {
                tooltipCallback({
                  active,
                  payload: cleanPayload,
                  label: labelString,
                });
                prevActiveRef.current = active;
                prevLabelRef.current = labelString;
              }

              return showTooltip && active ? (
                CustomTooltip ? (
                  <CustomTooltip
                    active={active}
                    payload={cleanPayload}
                    label={labelString}
                  />
                ) : (
                  <ChartTooltip
                    active={active}
                    payload={cleanPayload}
                    label={labelString}
                    valueFormatter={valueFormatter}
                  />
                )
              ) : null;
            }}
          />

          {showLegend ? (
            <RechartsLegend
              verticalAlign="top"
              height={legendHeight}
              content={({ payload }) =>
                customLegend
                  ? React.createElement(customLegend, {
                      payload: payload?.map((item: RechartsLegendPayloadItem) => {
                        const value = item.value ?? "";
                        return {
                          value,
                          color: getColorForCategory(value, categories.indexOf(value)),
                        };
                      }),
                      onClick: hasOnValueChange ? onCategoryClick : undefined,
                      activeLegend,
                    })
                  : ChartLegend(
                      { payload },
                      categoryColors,
                      setLegendHeight,
                      activeLegend,
                      hasOnValueChange
                        ? (clickedLegendItem: string) =>
                            onCategoryClick(clickedLegendItem)
                        : undefined,
                      enableLegendSlider,
                      legendPosition,
                      yAxisWidth,
                      getColorForCategory,
                    )
              }
            />
          ) : null}
          {categories.map((category, index) => (
            <Line
              className={cn(
                getColorClassName(
                  categoryColors.get(category) as AvailableChartColorsKeys,
                  "stroke",
                ),
              )}
              strokeOpacity={
                activeDot || (activeLegend && activeLegend !== category) ? 0.3 : 1
              }
              activeDot={(props: unknown) => {
                const dotProps = props as ActiveDotProps;
                const {
                  cx: cxCoord,
                  cy: cyCoord,
                  strokeLinecap,
                  strokeLinejoin,
                  strokeWidth,
                  dataKey,
                } = dotProps;
                const dataKeyString = dataKey ?? "";
                const categoryIndex = categories.indexOf(dataKeyString);
                const dotColor = getColorForCategory(dataKeyString, categoryIndex);
                const dotColorString = typeof dotColor === "string" ? dotColor : "";
                return (
                  <Dot
                    className={cn(
                      "stroke-white dark:stroke-gray-950",
                      onValueChange ? "cursor-pointer" : "",
                      dotColorString && !dotColorString.startsWith("#")
                        ? getColorClassName(dotColorString, "fill")
                        : undefined,
                    )}
                    cx={cxCoord ?? 0}
                    cy={cyCoord ?? 0}
                    r={5}
                    fill={dotColorString.startsWith("#") ? dotColorString : undefined}
                    stroke={dotColorString.startsWith("#") ? dotColorString : undefined}
                    strokeLinecap={
                      strokeLinecap as "butt" | "round" | "square" | "inherit" | undefined
                    }
                    strokeLinejoin={
                      strokeLinejoin as
                        | "round"
                        | "inherit"
                        | "miter"
                        | "bevel"
                        | undefined
                    }
                    strokeWidth={strokeWidth ?? 2}
                    onClick={(_, event) => onDotClick(dotProps, event)}
                  />
                );
              }}
              dot={false}
              key={category}
              name={category}
              type="linear"
              dataKey={category}
              stroke={getColorForCategory(category, index)}
              strokeWidth={2}
              strokeLinejoin="round"
              strokeLinecap="round"
              isAnimationActive={false}
              connectNulls={connectNulls}
            />
          ))}
          {/* hidden lines to increase clickable target area */}
          {onValueChange
            ? categories.map((category) => (
                <Line
                  className={cn("cursor-pointer")}
                  strokeOpacity={0}
                  key={category}
                  name={category}
                  type="linear"
                  dataKey={category}
                  stroke="transparent"
                  fill="transparent"
                  legendType="none"
                  tooltipType="none"
                  strokeWidth={12}
                  connectNulls={connectNulls}
                  onClick={(props: unknown, event: React.MouseEvent) => {
                    event.stopPropagation();
                    const lineProps = props as { name?: string };
                    const { name } = lineProps;
                    if (name) {
                      onCategoryClick(name);
                    }
                  }}
                />
              ))
            : null}
        </RechartsLineChart>
      </ResponsiveContainer>
    </div>
  );
});

LineChart.displayName = "LineChart";

export { LineChart, type LineChartEventProps, type TooltipProps };
